import { Button, Paper, Typography } from '@mui/material'
import type { ContentProposal } from '@shared/contentProposal'
import { proposalWordChanges } from '@shared/proposalWordChanges'
import { useMemo } from 'react'

export default function ChatProposalLink({
  item,
  onGoTo,
}: {
  item: ContentProposal
  onGoTo?: (filename: string, blockId: string, text: string) => void
}) {
  const remaining = useMemo(
    () =>
      item.status === 'pending' ? proposalWordChanges(item.currentText, item.markdown)?.changes.length : undefined,
    [item.status, item.currentText, item.markdown],
  )
  const accepted = item.decisions?.filter((decision) => decision.action === 'accept').length ?? 0
  const rejected = item.decisions?.filter((decision) => decision.action === 'reject').length ?? 0
  const mixed = (item.status === 'accepted' && rejected > 0) || (item.status === 'rejected' && accepted > 0)
  const status = mixed
    ? 'הטיפול הושלם · חלק מהשינויים אושרו וחלק נדחו'
    : item.status === 'pending' && remaining
      ? remaining === 1
        ? 'שינוי אחד ממתין'
        : `${remaining} שינויים ממתינים`
      : { pending: 'ממתינה לבדיקה', applying: 'בתהליך החלה', accepted: 'אושרה', rejected: 'נדחתה' }[item.status]
  const anchor =
    item.range.type === 'blocks'
      ? item.range.startBlockId
      : item.range.type === 'subtree'
        ? item.range.headingBlockId
        : (item.anchorBlockId ?? '')
  const excerpt = (item.originalText ?? item.currentText).replace(/\s+/g, ' ').trim()
  return (
    <Paper
      data-testid="chat-proposal-link"
      data-proposal-id={item.id}
      variant="outlined"
      sx={{ p: 1.25, my: 1, overflowWrap: 'anywhere' }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {item.filename}
      </Typography>
      {excerpt && (
        <Typography variant="caption" color="text.secondary" display="block">
          {excerpt.slice(0, 90)}
          {excerpt.length > 90 ? '…' : ''}
        </Typography>
      )}
      {item.reason && (
        <Typography variant="body2" sx={{ my: 0.75 }}>
          {item.reason}
        </Typography>
      )}
      <Typography variant="body2" role="status">
        {status}
      </Typography>
      {item.status === 'pending' && (accepted > 0 || rejected > 0) && (
        <Typography variant="caption" display="block">
          אושרו: {accepted} · נדחו: {rejected}
        </Typography>
      )}
      {item.status === 'pending' && !item.canApply && (
        <Typography variant="caption" display="block">
          לבדיקה בסביבת התוכן שבה הוכנה ההצעה
        </Typography>
      )}
      {onGoTo && (
        <Button size="small" onClick={() => onGoTo(item.filename, anchor, '')} sx={{ mt: 0.5 }}>
          הצג בעורך
        </Button>
      )}
    </Paper>
  )
}
